<script setup lang="ts">
import { onMounted, ref } from 'vue'
import ConfettiGenerator from 'confetti-js'

import type { ContestColor, ContestGameReward, GameContest } from '@/types'
import TheFooter from '@/components/TheFooter.vue'

import { hexToRgb } from '@/utils/color'
import BaseButton from '@/components/Base/BaseButton.vue'

const props = withDefaults(
  defineProps<{
    contestGame: GameContest
    contestColor: ContestColor
    gift: ContestGameReward | null
    webAppUrl: string
  }>(),
  {
    gift: () => ({
      id: '',
      value: '',
      type: '',
      color: '',
    }),
    contestColor: () => ({
      backgroundColor: '',
      buttonColor: '',
      textColor: '',
    }),
  },
)

const confetti = ref(null)
const showText = ref(false)

onMounted(() => {
  setAnimation()
})

const setAnimation = async () => {
  const windowWidth = window.innerWidth < 380 ? 396 : window.innerWidth
  const windowHeight = window.innerHeight < 350 ? 704 : window.innerHeight

  const colors = [
    hexToRgb(props.contestColor.backgroundColor),
    hexToRgb(props.contestColor.buttonColor),
    hexToRgb(props.contestColor.textColor),
  ]

  const confettiSettings = {
    target: 'animation',
    max: '450',
    size: '1.3',
    animate: true,
    props: ['circle', 'square'],
    colors,
    clock: '50',
    rotate: true,
    width: windowWidth,
    height: windowHeight,
    start_from_edge: false,
    respawn: true,
  }

  confetti.value = await new ConfettiGenerator(confettiSettings)
}
const render = async () => {
  document.body.style.overflow = 'hidden'

  showText.value = true
  await confetti.value?.render()
}

defineExpose({
  render,
})
</script>

<template>
  <div
    v-show="showText"
    id="animationContainer"
    ref="animationContainer"
    class="fixed z-50 h-full w-full"
    :style="{ backgroundColor: contestColor.backgroundColor }"
  >
    <canvas id="animation" class="fixed left-0 right-0 top-0" />
    <div class="absolute top-1/2 w-full -translate-y-1/2 transform p-6 text-center">
      <h1 class="text-4xl">
        <span class="mb-4 block text-5xl">😇</span>
        {{ contestGame.winningText }}
        <span v-if="gift" class="mt-4 block font-bold leading-tight">
          {{ gift.name }}
        </span>
      </h1>
      <h2 class="mt-10 text-2xl font-medium">
        {{ contestGame.sendingNotificationText }}
      </h2>
      <base-button
        v-if="webAppUrl && gift?.type === 'points'"
        :href="webAppUrl"
        class="mt-10 w-fit"
        is-active
      >
        {{ $t('buttons.goToWebApp') }}
      </base-button>
    </div>
    <TheFooter v-if="showText" medium="result" />
  </div>
</template>
